import { TFunction } from "i18next";
import { z } from "zod";

export const getRequisitionFormSchema = (t: TFunction) => {
  const invalidDate = t("warehouse.requisitions.validation.invalid-date");
  const equipmentRequired = t("warehouse.requisitions.validation.equipment-required");
  const required = t("warehouse.requisitions.validation.required");
  const invalidTime = t("warehouse.requisitions.validation.invalid-time");

  const requiredText = z.string().trim().min(1, required);
  const transportGuideSchema = z.object({
    project: requiredText,
    origin: requiredText,
    destination: requiredText,
    departureTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, invalidTime),
    vehicle: requiredText,
    licensePlate: requiredText,
    fiscalNumber: z.string().trim().min(1).optional(),
  });

  return z
    .object({
      equipmentIds: z
        .array(z.number().int().positive(), { message: equipmentRequired })
        .min(1, equipmentRequired),
      startDate: z.string().date(invalidDate).nullable().optional(),
      endDate: z.string().date(invalidDate).nullable().optional(),
      notes: z.string().nullable().optional(),
      autoReturn: z.boolean().default(false),
      transportGuide: transportGuideSchema.nullable().optional(),
    })
    .refine(
      (data) =>
        !data.startDate ||
        !data.endDate ||
        data.endDate >= data.startDate,
      {
        message: t("warehouse.requisitions.validation.end-before-start"),
        path: ["endDate"],
      },
    )
    .refine((data) => !data.autoReturn || !!data.endDate, {
      message: t("warehouse.requisitions.end-date-required-auto-return"),
      path: ["endDate"],
    })
    .refine((data) => !data.autoReturn || !!data.endDate, {
      message: t("warehouse.requisitions.end-date-required-auto-return"),
      path: ["endDate"],
    })
    .refine((data) => !data.transportGuide || !!(data.startDate && data.endDate), {
      message: t("warehouse.requisitions.validation.guide-requires-dates"),
      path: ["transportGuide"],
    });
};

export type RequisitionFormType = z.infer<
  ReturnType<typeof getRequisitionFormSchema>
>;
